import {useEffect, useRef, useState, useCallback, useMemo} from 'react';
import {useResourceUpdates as useResourceUpdatesCore} from '@ey/voltron-core';
import {manager} from '../resources/resources';
import {useDispatch, useSelector, shallowEqual} from 'react-redux';
import {useLocation, useSearchParams} from 'react-router-dom';
import {createSelector} from 'reselect';
import isEqual from 'lodash/isEqual';
import {labels, setThousandSeparator} from './utils';
import env from './env';
import {clearComments, updateCommentCountAfterClearingComments} from '../actions/EngagementComments/engagementcommentactions';
import {getUnresolvedCommentsCount, getDocumentValidations} from '../actions/ValidationControl/validationcontrolactions';
import {
	ENGAGEMENTMETADATA_URL,
	EngagementStatus,
	IndependenceAndProfileDocumentTypes,
	clientSideEventNames,
	documentsApiExpandableItems,
	engagementSourceId,
	entities,
	frameSubEvent,
	serviceURL,
	validationTypes,
	bodyTypesWithEmbeddedDocumentTypes,
	formBodyType,
	messageHub,
	messageType,
	documentTypes
} from './uiconstants';
import Utility from '../util/utilityfunctions';
import {getTaskDocumentByDocumentId} from '../selectors/Ares/selectors';
import {setDocumentId} from '../actions/Document/documentactions';
import {setApprovalsFromDocument} from '../actions/Task/signoffsaction';
import {expandableGetFormById, useAresUrlParameters, useIsAresView, useIsDrawerMode} from './Ares/customHooks';
import {getDocumentByDocumentId} from '../actions/Ares/taskDocumentActions';
import {raiseJSValidationError} from '../actions/erroractions';
import {getActiveAndGuestFormUsers} from '../actions/FormUsers/formusersactions';
import {getFormValidations} from '../components/Main/Ares/Tasks/TaskView/Common/guidedWorkflow.utils';
import {useFrameService, frameCommunicationEvents, frameEvents} from '@ey/canvascoreservices/ReactiveFrames';
import {useMessageHubEmit, useMessageHubListener} from '@ey/voltron-core';
import {getEngagement, getEngagementMetaData} from '../actions/Engagement/engagementactions';
import {getConfigurations} from '../actions/Configuration/configurationactions';
import {getQuickLinks} from '../actions/Engagement/quicklinksactions';
import {getMateriality} from '../actions/Materiality/materialityactions';
import {getFormBodyDataBySectionId} from '../selectors/Forms/formBodySelectors';
import {setDrawerFormDetails, updateActiveFrame, isDrawerCollapsed} from '../actions/AIChatbot/AIChatbotActions';
import {getValidationsForDocumentId} from '../selectors/Forms/canvasFormSelectors';

/**
 * Writes a line in the debug console each time the components re-renders, adding information about which props change caused the render.
 * (meant for internal debugging only. DO NOT USE IN PRODUCTION).
 * @param {string} name Component name used when logging the message.
 * @param {object} props props used in the component.
 */
export function useWhyDidYouUpdate(name, props) {
	// Get a mutable ref object where we can store props ...
	// ... for comparison next time this hook runs.
	const previousProps = useRef();

	useEffect(() => {
		if (!env.isProd()) {
			if (previousProps.current) {
				// Get all keys from previous and current props
				const allKeys = Object.keys({...previousProps.current, ...props});
				// Use this object to keep track of changed props
				const changesObj = {};
				// Iterate through keys
				allKeys.forEach((key) => {
					// If previous is different from current
					if (previousProps.current[key] !== props[key]) {
						// Add to changesObj
						changesObj[key] = {
							from: previousProps.current[key],
							to: props[key]
						};
					}
				});

				// If changesObj not empty then output to console
				if (Object.keys(changesObj).length) {
					console.log('[why-did-you-update]', name, changesObj);
				}
			}

			// Finally update previousProps with current props for next hook call
			previousProps.current = props;
		}
	});
}

/**
 * Retrieves the user preferences from the redux store.
 * @returns {Object} The user preferences.
 */
export function useUserPreferences() {
	return useSelector((state) => state.userPreferences, isEqual);
}

/**
 * Determines whether the current user is a CEAP user.
 * @returns {boolean} 'true' if the user is a CEAP user, 'false' otherwise.
 */
export function useIsCEAPUser() {
	return useSelector((state) => !!state.userPreferences?.isCEAPUser);
}

/**
 * Determines whether the current user is a CCP user.
 * @returns {boolean} 'true' if the user is a CCP user, 'false' otherwise.
 */
export function useIsCCPUser() {
	return useSelector((state) => !!state.userPreferences?.isGuestUser && !state.userPreferences?.isEYUser);
}

/**
 * Determines whether the current user has the Internal Review Only role.
 * @returns {boolean} 'true' if the user has the Internal Review Only role, 'false' otherwise.
 */
export function useIsReviewOnlyUser() {
	return useSelector((state) => !!state.userPreferences?.permissions?.hasInternalReviewOnlyRole);
}

/**
 * Determines whether the current user is a guest user.
 * @returns {boolean} 'true' if the user is a guest user, 'false' otherwise.
 */
export function useIsGuestUser() {
	return useSelector((state) => !!state.userPreferences?.isGuestUser);
}

/**
 * Returns the number separators to be used when formatting numbers, according to the user settings.
 * @returns {Object} Returns the number separators to be used when formatting numbers.
 */
export function useNumberFormatSettings() {
	const userPreferences = useUserPreferences();
	return setThousandSeparator(userPreferences.numberFormat);
}

/**
 * Returns the current Engagement Id.
 * @returns {Number | String} The current Engagement ID.
 */
export function useEngagementId() {
	let engagementId = useSelector((state) => Number(state.engagementId));
	if (!engagementId) {
		engagementId = Utility.getEngagementIdFromUrl(window);
		if (engagementId && engagementId instanceof Array) {
			engagementId = engagementId[0];
		}
		engagementId = Number(engagementId);
	}
	return engagementId;
}

/**
 * Determines wheter the engagement Helix project(s) are allowed to use deeplink analyzer types.
 * @returns {boolean} 'true' if allowed, 'false' otherwise.
 */
export function useIsAllowedDeepLinkAnalyzerType() {
	const helixProjects = useSelector((store) => store.helixProjects, shallowEqual);
	const engagementHelixProjects = useSelector((store) => store.engagementHelixProjects, shallowEqual);
	const configurations = useSelector((state) => state.configurations, shallowEqual);

	const allowedTypes = configurations?.features?.deeplinkAllowedAnalyzerTypes ?? [];
	const isAllowed = (analyzerType) => analyzerType && allowedTypes.includes(analyzerType.toLowerCase());

	if (!engagementHelixProjects || !allowedTypes.length) {
		return false;
	}

	if (engagementHelixProjects && engagementHelixProjects.length > 1 && helixProjects && helixProjects.length > 0) {
		return helixProjects?.some((project) => isAllowed(project?.helixAnalyzerType));
	}

	const helixConnection = engagementHelixProjects.find((x) => x.isPrimary);
	return isAllowed(helixConnection?.helixAnalyzerType);
}

/**
 * Returns the current Engagement.
 * @returns {Object} The current Engagement.
 */
export function useEngagement() {
	return useSelector((state) => state.engagement, isEqual);
}

/**
 * Determines whether the current engagement is a CEAP engagement.
 * @returns {boolean} 'true' if the engagement source is CEAPV2, CEAPV2Protected, or CopyEngagementReviewOnly, 'false' otherwise.
 */
export function useIsCEAPEngagement() {
	const engagementSource = useSelector((state) => state.engagement?.engagementSourceId);
	return (
		engagementSource === engagementSourceId.CEAPV2 ||
		engagementSource === engagementSourceId.CEAPV2Protected ||
		engagementSource === engagementSourceId.CopyEngagementReviewOnly
	);
}

/**
 * Returns the current Engagement Metadata.
 * @param {Number} engagementId The Engagement ID.
 * @returns {Object} The current Engagement Metadata.
 */
export function useEngagementMetadata(engagementId) {
	const metadata = useSelector((state) => state.engagementMetaData);
	if (metadata?.totalpageditems === 0) {
		return {datacenterid: 0};
	} else {
		return metadata?.engagementmetadatadetails?.find((x) => x.engagementid === engagementId) ?? {datacenterid: 0};
	}
}

/**
 * Returns the current configurations
 * @returns {Object} the current Configurations
 */
export function useConfigurations() {
	return useSelector((state) => state.configurations);
}

/**
 * Returns the current errors in the application.
 * @returns {Object | String} The current errors.
 */
export function useErrors() {
	return useSelector((state) => state.errors);
}

/**
 * Returns the document's User Presence.
 * @param {String} documentId The Document ID.
 * @returns {Object | String} The document's User Presence.
 */
export function useUserPresence(documentId) {
	return useSelector((state) => state.userPresence[documentId]);
}

/**
 * Returns the current Document Id.
 * @returns {String} The current Document ID.
 */
export function useDocumentId() {
	let documentId = useSelector((state) => state.documentId);

	if (!documentId) {
		const location = useLocation();
		documentId = Utility.getDocumentIdFromUrl({location});
	}

	return documentId;
}

/**
 * Returns the parent Smart Evidence Source ID from the Ares form headers.
 * @returns {String|Number|undefined} The parent Smart Evidence Source ID.
 */
export function useParentSmartEvidenceSourceId() {
	let parentSmartEvidenceSourceId = useSelector((state) => state?.aresFormHeaders?.parentSmartEvidenceSourceId);

	return parentSmartEvidenceSourceId;
}

/**
 * Returns the current document Engagement Id.
 * @returns {Number | String} The document Engagement Id.
 */
export function useDocumentEngagementId() {
	return useCanvasFormMetadata().documentEngagementId;
}

/**
 * Helper function to retrieve the current document from the Redux state.
 * @param {Object} state - The Redux state object.
 * @returns {Object} The current document object.
 */
function getDocument(state) {
	const documentId = state.documentId || Utility.getDocumentIdFromUrl({location: window.location});
	return getTaskDocumentByDocumentId(state, documentId);
}

/**
 * Helper function to retrieve the engagement ID from the Redux state.
 * @param {Object} state - The Redux state object.
 * @returns {Number|String} The engagement ID.
 */
function getEngagementId(state) {
	return state.engagementId;
}

/**
 * Memoized selector to retrieve form metadata from document and engagement ID.
 * @returns {Object} Object containing isShared, isTargetEngagement, and documentEngagementId.
 */
const getFormMetadata = createSelector([getDocument, getEngagementId], (document, engagementId) => {
	return {
		isShared: document?.isShared,
		hasLinkedResponse: document?.hasLinkedResponse,
		isTargetEngagement: document?.engagementId !== Number(engagementId),
		documentEngagementId: Number(document?.engagementId),
		documentType: document?.documentTypeId
	};
});

/**
 * Returns basic metadata about the current Canvas Form being displayed on the UI.
 * @returns {Object} Current Canvas Form metadata.
 */
export function useCanvasFormMetadata() {
	return useSelector(getFormMetadata);
}

/**
 * Custom hook to add and cleanup window event listeners.
 * Automatically removes the event listener when the component unmounts or callback changes.
 * @param {string} eventName - The name of the window event to listen to.
 * @param {Function} callback - The callback function to execute when the event is triggered.
 */
export function useWindowEventListener(eventName, callback) {
	useEffect(() => {
		window.addEventListener(eventName, callback);
		return () => {
			window.removeEventListener(eventName, callback);
		};
	}, [callback]);
}

/**
 * Custom hook to manage clearing all comments for a document or entity.
 * Handles clearing comments for various entity types (FormHeader, FormSection, FormBody, Document)
 * and updates validation counts accordingly.
 * @param {Number|String} engagementId - The engagement ID.
 * @param {String} documentId - The document ID.
 * @param {Number} documentTypeId - The document type ID.
 * @param {Number} entityId - The entity ID (from entities constants).
 * @param {String|Number} entityUid - The unique identifier for the entity.
 * @param {Function} callback - Optional callback function to execute after clearing comments.
 * @param {Number} [bodyTypeId] - Optional body type ID for filtering specific body types.
 * @returns {Object} Object containing setClearAllComments, setEntityId, and setEntityUid functions.
 */
export function useClearAllComments(engagementId, documentId, documentTypeId, entityId, entityUid, callback, bodyTypeId = undefined) {
	const formBodyData = useSelector((store) => store.formBodyData),
		comments = useSelector((store) => store.engagementComments),
		formData = useSelector((state) => getTaskDocumentByDocumentId(state, documentId)),
		entityDocuments = useSelector((store) => store.entityDocuments),
		allValidations = useSelector((store) => store.validations);
	const dispatch = useDispatch();
	const [clearAllComments, setClearAllComments] = useState(false),
		[_entityId, setEntityId] = useState(entityId),
		[_entityUid, setEntityUid] = useState(entityUid);

	const execute = async () => {
		const documentWithUnresolvedComments = [];
		let bodyTypeIds = [];
		if (entityId === entities.FormSection && entityUid) {
			let sectionBodies = formBodyData.filter((fbd) => fbd.sectionId === Number(entityUid));
			if (sectionBodies) {
				bodyTypeIds.push(...new Set(sectionBodies[0].bodies?.map((body) => body.bodyTypeId)));
				if (bodyTypeIds.length > 0) {
					for (const bodyTypeId of bodyTypeIds) {
						const documentsWithUnresolvedComments = getDocumentsWithUnresolvedComments(bodyTypeId, entityDocuments, allValidations);
						documentWithUnresolvedComments.push(...documentsWithUnresolvedComments);
					}
				}
			}
		} else if (bodyTypeId) {
			const unresolvedComments = getDocumentsWithUnresolvedComments(bodyTypeId, entityDocuments, allValidations);
			documentWithUnresolvedComments.push(...unresolvedComments);
		}
		await dispatch(clearComments(engagementId, documentId, _entityId, _entityUid.toString())).then(async () => {
			if (documentId) {
				await clearAllCommentsOnEmbeddedDocuments(documentWithUnresolvedComments).then((results) => {
					dispatch(getUnresolvedCommentsCount(engagementId, documentId, documentTypeId, undefined, formData?.embeddedBodyTypeIds))
						.then(() => {
							if (formBodyData && formBodyData.length > 0) {
								switch (_entityId) {
									case entities.FormHeader: {
										/*find sections for headerId*/
										let sections = formData.headers.filter((fl) => {
											return Number(fl.id) === Number(_entityUid);
										})[0].sections;
										if (sections && sections.length > 0) {
											if (sections) {
												let bodyIds = [];
												sections.map((s) => {
													let section = formBodyData.filter((fbd) => {
														return fbd.sectionId === s.id;
													});
													if (section && section.length > 0) {
														let sectionsBodies = section[0].bodies?.map((body) => body.id);
														bodyIds.push(...sectionsBodies);
													}
												});
												if (bodyIds.length > 0) {
													dispatch(updateCommentCountAfterClearingComments(bodyIds));
												}
											}
										}
										break;
									}
									case entities.FormSection: {
										let sectionBodies = formBodyData.filter((fbd) => fbd.sectionId === Number(_entityUid));
										if (sectionBodies) {
											let bodyIds = [];
											bodyIds.push(sectionBodies[0].bodies?.map((body) => body.id));
											dispatch(updateCommentCountAfterClearingComments(bodyIds[0]));
										}
										break;
									}
									case entities.FormBody: {
										dispatch(updateCommentCountAfterClearingComments([_entityUid]));
										break;
									}
									case entities.Document: {
										let bodyIds = [];
										if (Object.keys(comments) && Object.keys(comments).length > 0) {
											Object.keys(comments).map((key) => {
												if (parseInt(key)) {
													bodyIds.push(parseInt(key));
												}
											});
										}
										if (bodyIds.length > 0) {
											dispatch(updateCommentCountAfterClearingComments(bodyIds));
										}
									}
								}
							}
						})
						.then(() => {
							if (documentWithUnresolvedComments.length > 0) {
								dispatch(getDocumentValidations(engagementId, documentWithUnresolvedComments));
							}
						});
				});
			}
			if (callback) {
				callback();
			}
		});
	};

	const clearAllCommentsOnEmbeddedDocuments = (documentWithUnresolvedComments) => {
		let callArray = documentWithUnresolvedComments.map((doc) => {
			return dispatch(clearComments(engagementId, doc.id, entities.Document, doc.id.toString()));
		});
		return Promise.allSettled(callArray);
	};

	const getDocumentsWithUnresolvedComments = (bodyTypeId, entityDocuments, allValidations) => {
		const documentWithUnresolvedComments = [];
		if (bodyTypeId) {
			const bodyData = bodyTypesWithEmbeddedDocumentTypes.find((body) => body.bodyTypeId === bodyTypeId);
			if (bodyData) {
				const embeddedDocuments = Object.values(entityDocuments.byId).filter((e) => e.documentTypeId === bodyData.documentTypeId);
				embeddedDocuments.forEach((document) => {
					const validation = allValidations[document.id];
					if (validation) {
						const hasUnresolvedComment = validation.find((v) => v.validationTypeId === validationTypes.CanvasFromComments && v.validationCount > 0);
						if (hasUnresolvedComment) {
							documentWithUnresolvedComments.push(document);
						}
					}
				});
			}
		}
		return documentWithUnresolvedComments;
	};

	useEffect(() => {
		if (clearAllComments === true) {
			execute();
		}
	}, [clearAllComments]);

	useEffect(() => {
		setEntityUid(entityUid);
	}, [entityUid]);
	return {setClearAllComments, setEntityId, setEntityUid};
}

/**
 * Provides a form values state object, and a callback to update the state.
 * @param {object} initialState The form's initial state.
 * @returns {Array} An array containing the state object, and a useCallback function that updates
 * the consumer component's state accoringly.
 */
export function useForm(initialState = {}) {
	const [state, setState] = useState(initialState);

	const _setState = useCallback((name, value) => {
		// Updates the state, based on the previous value
		setState((prevState) => ({
			...prevState,
			[name]: value
		}));
	}, []);

	return [state, useCallback((name, value) => _setState(name, value), [_setState])];
}

/**
 * Custom hook to manage modal visibility state for multiple modals.
 * Each modal is tracked by its entity ID.
 * @param {Object} [initialState={}] - Initial state object mapping entity IDs to visibility boolean.
 * @returns {Array} Array containing the state object and a toggle function.
 */
export const useModal = (initialState = {}) => {
	const [show, setShow] = useState(initialState);
	return [show, useCallback((entityId) => setShow((state) => ({...state, [entityId]: !state[entityId]})), [])];
};

/**
 * Gets the entity ID of the currently shown modal from the modal state.
 * @param {Object} state - The modal state object.
 * @returns {String|undefined} The entity ID of the shown modal, or undefined if no modal is shown.
 */
export const getShownModalEntityId = (state) => Object.entries(state).find(([_, value]) => value) && Object.entries(state).find(([_, value]) => value)[0];

/**
 * Custom hook to help parsing query string parameters from the url.
 * @returns {Object} Object with query string parameters.
 */
export function useQuery() {
	const {search} = useLocation();

	return useMemo(() => {
		const searchParams = new URLSearchParams(search);
		const queryStringParameters = {};
		for (const [name, value] of searchParams.entries()) {
			if (name.toLowerCase() !== 'referrer') {
				queryStringParameters[name.toLowerCase()] = value;
			}
		}
		return queryStringParameters;
	}, [search]);
}

/**
 * Custom hook to retrieve quick links from the Redux store.
 * @returns {Object} The quick links object.
 */
export function useQuickLinks() {
	return useSelector((state) => state.quickLink);
}

/**
 * Custom hooks to help switch between document within the same engagement.
 * @param {Object} options object.
 * @returns {Object} Object that contains switchDocument function.
 */
export function useDocumentSwitcher({includeApprovals = false, includeReviewNotes = false, ensureUsers = false}) {
	const dispatch = useDispatch();
	const engagementId = useEngagementId();
	const isAresView = useIsAresView();
	const [isMFE] = useMFE();
	const isModalOpen = useIsModalOpen();
	const emit = useMessageHubEmit(messageHub.kronosError);

	const engagement = useSelector((store) => store.engagement);

	const isMestEngagement = useMemo(() => engagement?.isMultiEntityEngagement, [engagement]);

	const currentEngagment = useMemo(() => {
		if (!engagement) {
			return {engagementId};
		} else if (engagement && !engagement.engagementId) {
			return {...engagement, engagementId};
		} else {
			return engagement;
		}
	}, [engagement, engagementId]);

	const switchDocument = useCallback(
		(documentId) => {
			dispatch(setDocumentId(documentId));

			const promises = [];

			const getDocumentByIdParameters = {
				expand: documentsApiExpandableItems.tasks,
				includeApprovals,
				includeReviewNotes
			};

			getDocumentByIdParameters.includeRelatedEntities = true;

			let firstDocumentStEntityId;

			const getDocumentById = new Promise((resolve) => {
				dispatch(getDocumentByDocumentId(engagementId, documentId, getDocumentByIdParameters, isAresView)).then((document) => {
					if (document.entities?.length > 0) {
						firstDocumentStEntityId = document.entities[0].id;
					}
					return resolve(document);
				});
			});

			promises.push(getDocumentById);

			promises.push(dispatch(expandableGetFormById(currentEngagment, documentId, undefined, {resolve: true}, undefined, isAresView)));

			const promise = Promise.all(promises)
				.then((responses) => {
					const document = responses[0];

					if (document && document.approvals && document.approvals.length > 0) {
						dispatch(setApprovalsFromDocument(document.approvals, document.id));
					}

					const form = responses[1][0];

					getFormValidations(engagementId, document, undefined, dispatch, form.stEntityId);

					if (!IndependenceAndProfileDocumentTypes.includes(document.documentTypeId)) {
						const queryParameters = firstDocumentStEntityId ? {stEntityId: firstDocumentStEntityId} : undefined;

						return dispatch(getMateriality(engagementId, undefined, queryParameters));
					}
				})
				.catch(() => {
					dispatch(raiseJSValidationError(emit, labels.invalidDocumentId, isMFE, isModalOpen));
				});

			if (ensureUsers) {
				dispatch(getActiveAndGuestFormUsers(Number(engagementId), documentId));
			}

			return promise;
		},
		[dispatch, includeApprovals, includeReviewNotes, currentEngagment, isAresView, ensureUsers, engagementId, emit, isMFE, isModalOpen]
	);

	return useMemo(() => {
		return {
			switchDocument
		};
	}, [switchDocument]);
}

/**
 * Custom hook to subscribe to frame events and manage form details in drawer mode.
 * Handles frame communication events like form details, active frame changes, and drawer toggle.
 * Updates Redux store with drawer form details and active frame information.
 */
export function useFormDetails() {
	const dispatch = useDispatch();
	const frameService = useFrameService();
	const subscriptionRef = useRef(null);
	useEffect(() => {
		subscriptionRef.current = frameService.subscribeFrame({
			next: (args) => {
				switch (args.event) {
					case frameCommunicationEvents.MESSAGE:
						if (args.subEvent === frameSubEvent.FORM_DETAILS) {
							dispatch(setDrawerFormDetails({detail: [{...args, isActiveFrame: true}]}));
						}
						break;
					case frameEvents.ACTIVE_FRAME:
						dispatch(updateActiveFrame({frameKey: args.key, isFrameClose: false}));
						break;
					case frameEvents.DRAWER_TOGGLED:
						if (args.open) {
							dispatch(isDrawerCollapsed(false));
						} else {
							dispatch(isDrawerCollapsed(true));
						}
						break;
					case frameEvents.REMOVE:
					case frameEvents.CLEAR_FRAMES:
						dispatch(updateActiveFrame({frameKey: args.key, isFrameClose: true}));
						break;
				}
			}
		});

		return () => {
			if (subscriptionRef.current && !subscriptionRef.current.closed) {
				subscriptionRef.current.unsubscribe();
			}
		};
	}, []);
}

/**
 * Custom hook to manage menu click events in drawer mode.
 * Listens for menu expansion/collapse events from frames and handles click events
 * to close the menu when clicking outside in drawer mode.
 */
export function useMenuClick() {
	const [isMenuExpanded, setIsMenuExpanded] = useState(false);

	const isDrawerMode = useIsDrawerMode();

	const frameService = useFrameService();

	const subscriptionRef = useRef(null);

	const clickListener = useCallback(() => {
		frameService.sendMessageToParent({subEvent: frameSubEvent.CLOSE_MENU});
	}, [frameService]);

	useEffect(() => {
		subscriptionRef.current = frameService.subscribeFrame({
			next: (args) => {
				switch (args.event) {
					case frameCommunicationEvents.MESSAGE:
						if (args.subEvent === frameSubEvent.MORE_MENU_CLICK) {
							setIsMenuExpanded(args.isExpanded);
						}

						if (args.subEvent === frameSubEvent.CLOSE_MENU) {
							window.dispatchEvent(new CustomEvent('closeMenu'));
						}
						break;
				}
			}
		});

		return () => {
			if (subscriptionRef.current && !subscriptionRef.current.closed) {
				subscriptionRef.current.unsubscribe();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (isMenuExpanded && isDrawerMode && window.top !== window.self) {
			window.addEventListener('click', clickListener, false);
		}

		return () => {
			window.removeEventListener('click', clickListener, false);
		};
	}, [clickListener, isDrawerMode, isMenuExpanded]);
}

/**
 * Custom hook to manage automatic refresh of form body data when drawer is toggled or closed.
 * Listens to drawer toggle and frame removal events to trigger appropriate refresh actions
 * based on the body type (account-based bodies require special handling).
 */
export function useRefresh() {
	const subscriptionRef = useRef(null);

	const frameService = useFrameService();

	const {headerId, sectionId} = useAresUrlParameters();
	const bodies = useSelector((store) => getFormBodyDataBySectionId(store, sectionId));

	const needsAccountRefresh = useMemo(
		() =>
			bodies &&
			bodies.find(
				(body) =>
					body.bodyTypeId === formBodyType.ALRAAccountList ||
					body.bodyTypeId === formBodyType.AuditPlanAccountListing ||
					body.bodyTypeId === formBodyType.ALRASummary
			) !== undefined,
		[bodies]
	);

	const dispatchEvent = useCallback(
		({headerId, sectionId}) => {
			if (needsAccountRefresh) {
				window.dispatchEvent(new CustomEvent(clientSideEventNames.getAccountsForBody));
			} else {
				window.dispatchEvent(
					new CustomEvent(clientSideEventNames.getBodiesBySectionId, {
						detail: {headerId, sectionId}
					})
				);
			}
		},
		[needsAccountRefresh]
	);

	useEffect(() => {
		if (Number.isFinite(headerId) && Number.isFinite(sectionId) && headerId !== 0 && sectionId !== 0) {
			subscriptionRef.current = frameService.subscribeFrame({
				next: (args) => {
					switch (args.event) {
						case frameEvents.DRAWER_TOGGLED:
							if (!args.open && !args.isMoreMenuClicked) {
								dispatchEvent({headerId, sectionId});
							}
							break;
						case frameEvents.REMOVE:
							if (args.last) {
								dispatchEvent({headerId, sectionId});
							}
							break;
					}
				}
			});
		}

		return () => {
			if (subscriptionRef.current && !subscriptionRef.current.closed) {
				subscriptionRef.current.unsubscribe();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatchEvent, headerId, sectionId]);
}

/**
 * Custom hook to track the active frame state and key in drawer mode.
 * Subscribes to frame events to monitor when frames are activated, removed, or toggled.
 * @returns {Object} Object containing toggled (boolean) and frameKey (string|null) properties.
 */
export function useActiveFrame() {
	const [toggled, setToggled] = useState(false);

	const [frameKey, setFrameKey] = useState(null);

	const frameService = useFrameService();

	const subscriptionRef = useRef(null);

	useEffect(() => {
		subscriptionRef.current = frameService.subscribeFrame({
			next: (args) => {
				switch (args.event) {
					case frameEvents.ACTIVE_FRAME:
						setToggled(true);
						setFrameKey(args.key);
						break;

					case frameEvents.REMOVE:
					case frameEvents.CLEAR_FRAMES:
						setToggled(false);
						setFrameKey(null);
						break;

					case frameEvents.DRAWER_TOGGLED:
						setToggled(args.open);
						break;
				}
			}
		});

		return () => {
			if (subscriptionRef.current && !subscriptionRef.current.closed) {
				subscriptionRef.current.unsubscribe();
			}
		};
	}, []);

	return {toggled, frameKey};
}

/**
 * Custom hook to manage frame title updates in drawer mode.
 * Listens for UPDATE_FRAME_TITLE messages from child frames and updates the frame's
 * title and deep link source accordingly.
 */
export function useFrameTitle() {
	const frameService = useFrameService();

	const subscriptionRef = useRef(null);

	const updateFrameTitle = useCallback(({name, deepLinkSrc, frameKey}) => {
		if (frameKey) {
			frameService.updateFrame(frameKey, {
				name,
				deepLinkSrc
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		subscriptionRef.current = frameService.subscribeFrame({
			next: (args) => {
				switch (args.event) {
					case frameCommunicationEvents.MESSAGE:
						if (args.subEvent === frameSubEvent.UPDATE_FRAME_TITLE && args.updateFrameTitle) {
							updateFrameTitle(args);
						}
						break;
				}
			}
		});

		return () => {
			if (subscriptionRef.current && !subscriptionRef.current.closed) {
				subscriptionRef.current.unsubscribe();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateFrameTitle]);
}

/**
 * Custom hook to manage navigation-related data loading in drawer mode.
 * Automatically fetches engagement metadata, engagement information, feature configurations,
 * and quick links based on user permissions and independence information.
 * Only operates when user is an internal user with independence information and in drawer mode.
 */
export function useNav() {
	const internalUser = useSelector((store) => store.internalUser);
	const engagementMetadata = useSelector((store) => store.engagementMetaData);
	const engagement = useSelector((store) => store.engagement);
	const isAresView = useSelector((store) => store.isAresView);
	const user = useUserPreferences();
	const engagementId = useEngagementId();
	const dispatch = useDispatch();
	const isDrawerMode = useIsDrawerMode();
	const [isMFE] = useMFE();
	const isModalOpen = useIsModalOpen();
	const emit = useMessageHubEmit(messageHub.kronosError);

	const isInternalUserWithIndependence = useMemo(() => {
		return internalUser && internalUser.independenceInformation ? true : false;
	}, [internalUser]);

	const getEngagementMetadata = useCallback(() => {
		const engagementId = Utility.getEngagementIdFromUrl(window);

		/**
		 * when multiple datacenters are allowed we will set showdatacenterengagementsonly eq false and
		 * will have to change the url creation above in bindEngagementOptions
		 */

		const engagementStatusIds = [
			EngagementStatus.Active,
			EngagementStatus.Restored,
			EngagementStatus.MarkedForDeletion,
			EngagementStatus.CanvasExternalAccessPortalV2
		];

		const engagementStatusId = `engagementstatusid eq '${engagementStatusIds.join(',')}'`;

		// eslint-disable-next-line max-len
		const query = `filters=engagementid eq ${engagementId},showdatacenterengagementsonly eq true,showaccessibleengagementsonly eq true, ${engagementStatusId}`;

		dispatch(getEngagementMetaData(env.getUniversalUrl(`${ENGAGEMENTMETADATA_URL}?${query}`)));
	}, [dispatch]);

	const getEngagementInformation = useCallback(() => {
		const isEngagementAccessible = internalUser?.independenceInformation?.isEngagementAccessible;

		dispatch(getEngagement(engagementId, isEngagementAccessible)).catch((result) => {
			if (result && result.response && result.response.status === 403) {
				dispatch(raiseJSValidationError(emit, labels.invalidEngagementId, isMFE, isModalOpen));
			}
		});
	}, [dispatch, engagementId, internalUser?.independenceInformation?.isEngagementAccessible, emit, isMFE, isModalOpen]);

	const getFeatureConfigurations = useCallback(() => {
		return dispatch(getConfigurations(env.getBaseURL(serviceURL) + 'FeatureConfigurations'));
	}, [dispatch]);

	const getEngagementQuickLinks = useCallback(() => {
		return dispatch(getQuickLinks(Number(engagementId))).catch((result) => {
			if (result && result.response && result.response.status === 403) {
				dispatch(raiseJSValidationError(emit, labels.invalidEngagementId, isMFE, isModalOpen));
			}
		});
	}, [dispatch, engagementId, emit, isMFE]);

	useEffect(() => {
		if (isInternalUserWithIndependence) {
			if (user && user.id && !user.isGuestUser) {
				if (!engagementMetadata) {
					getEngagementMetadata();
				}
			}
		}
	}, [engagementMetadata, isInternalUserWithIndependence, user, getEngagementMetadata]);

	useEffect(() => {
		if (isInternalUserWithIndependence && isDrawerMode) {
			if (!isAresView && !engagement?.engagementId) {
				getEngagementInformation();
			}
		}
	}, [isDrawerMode, engagement?.engagementId, isInternalUserWithIndependence, isAresView, getEngagementInformation]);
}

/**
 * Custom hook to trace and log which props changed between renders.
 * Useful for debugging to understand why a component is re-rendering.
 * Logs to console with the component key and the changed props.
 * @param {Object} props - The component's props object to track.
 * @param {String} key - A unique identifier for the component (used in console logs).
 */
export function useTraceUpdate(props, key) {
	const prev = useRef(props);
	useEffect(() => {
		const changedProps = Object.entries(props).reduce((ps, [k, v]) => {
			if (prev.current[k] !== v) {
				ps[k] = [prev.current[k], v];
			}
			return ps;
		}, {});
		if (Object.keys(changedProps).length > 0) {
			console.log(`${key} - changed props:`, changedProps);
		}
		prev.current = props;
	});
}

/**
 * Custom hook to retrieve the task ID from query parameters or the document.
 * Prioritizes taskid from query string, falls back to taskId from document.
 * @param {String} documentId - The document ID to look up if taskid is not in query.
 * @returns {String|Number|undefined} The task ID.
 */
export function useTaskId(documentId) {
	const currentDocument = useSelector((store) => getTaskDocumentByDocumentId(store, documentId));
	const query = useQuery();

	return query?.taskid ?? currentDocument?.taskId;
}

/**
 * Check if the application is running in MFE (Micro Frontend) mode.
 * This is a non-hook utility that can be used outside React components.
 * @returns {boolean} true if running in MFE mode, false otherwise
 */
export function checkIsMFE() {
	const element = document.querySelector('[data-mfe]');
	let isMFE = !!element;

	// Check if the current domain is the Kronos web URL
	if (isMFE) {
		const kronosWebUrl = env.getURL('kronos_web');
		if (kronosWebUrl) {
			const currentDomain = window.location.origin;
			const kronosDomain = new URL(kronosWebUrl).origin;
			if (currentDomain === kronosDomain) {
				isMFE = false;
			}
		}
	}

	return isMFE;
}

// @deprecated - Use getMFEAttributes directly from Utility instead of this hook, since it doesn't have any React dependencies and can be used in non-React contexts.
export function useMFE() {
	return Utility.getMFEAttributes();
}

/**
 * Custom hook to retrieve the modal open state from the Redux store.
 * @returns {Boolean} True if a modal is currently open, false otherwise.
 */
export function useIsModalOpen() {
	return useSelector((state) => state.isModalOpen);
}

/**
 * Custom hook to manage session storage with engagement and document-specific keys.
 * Provides methods to get/set session storage items with properly namespaced keys
 * in the format: ares_engagement_{engagementId}_document_{documentId}_{storageKey}.
 * @returns {Object} Object containing getDocumentKey, setSessionStorageItem, and getSessionStorageItem functions.
 */
export const useSessionStorage = () => {
	const engagementId = useEngagementId();

	/**
	 * Generates a namespaced session storage key for a specific engagement and document.
	 * @param {String|Number} documentId - The document ID.
	 * @param {String} storageKey - The storage key identifier.
	 * @returns {String} The formatted session storage key in the format: ares_engagement_{engagementId}_document_{documentId}_{storageKey}.
	 */
	const getSessionStorageKey = useCallback(
		(documentId, storageKey) => {
			return `ares_engagement_${engagementId}_document_${documentId}_${storageKey}`;
		},
		[engagementId]
	);

	/**
	 * Stores data in session storage with a namespaced key.
	 * Automatically stringifies the data as JSON before storing.
	 * Logs errors to console if storage fails.
	 * @param {String|Number} documentId - The document ID.
	 * @param {String} storageKey - The storage key identifier.
	 * @param {*} data - The data to store (will be JSON stringified).
	 */
	const setSessionStorageItem = useCallback(
		(documentId, storageKey, data) => {
			try {
				sessionStorage.setItem(getSessionStorageKey(documentId, storageKey), JSON.stringify(data));
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(':( set storage error : ', error);
			}
		},
		[getSessionStorageKey]
	);

	/**
	 * Retrieves and parses data from session storage using a namespaced key.
	 * Automatically parses JSON data before returning.
	 * Logs errors to console if retrieval or parsing fails.
	 * @param {String|Number} documentId - The document ID.
	 * @param {String} storageKey - The storage key identifier.
	 * @returns {*|null} The parsed data from session storage, or null if not found or on error.
	 */
	const getSessionStorageItem = useCallback(
		(documentId, storageKey) => {
			try {
				const data = sessionStorage.getItem(getSessionStorageKey(documentId, storageKey));

				if (data) {
					return JSON.parse(data);
				}

				return null;
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(':( get storage error : ', error);
			}
		},
		[getSessionStorageKey]
	);

	/**
	 * Removes an item from session storage using a namespaced key.
	 * Logs errors to console if removal fails.
	 * @param {String|Number} documentId - The document ID.
	 * @param {String} storageKey - The storage key identifier.
	 */
	const clearSessionStorageItem = useCallback(
		(documentId, storageKey) => {
			try {
				sessionStorage.removeItem(getSessionStorageKey(documentId, storageKey));
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(':( clear storage error : ', error);
			}
		},
		[getSessionStorageKey]
	);

	return useMemo(() => {
		return {
			getDocumentKey: getSessionStorageKey,
			setSessionStorageItem,
			getSessionStorageItem,
			clearSessionStorageItem
		};
	}, [getSessionStorageKey, setSessionStorageItem, getSessionStorageItem, clearSessionStorageItem]);
};

export const useSingleBodyViewMode = () => {
	const [searchParams] = useSearchParams();

	const bodyId = searchParams.get('bodyid') || searchParams.get('bodyId');

	return Number(bodyId) > 0;
};

export const useGetValidationCounts = (validationTypeIds) => {
	const {documentId} = useAresUrlParameters();
	const validations = useSelector((store) => getValidationsForDocumentId(store, documentId));
	const validationTypeIdArray = Array.isArray(validationTypeIds) ? validationTypeIds : [validationTypeIds];
	return validations?.filter((v) => validationTypeIdArray.includes(v.validationTypeId)).reduce((acc, curr) => acc + (curr.validationCount || 0), 0);
};

/**
 * Custom hook for handling task details drawer navigation
 * Opens a task drawer in MFE mode or navigates to task URL in legacy mode
 * @param {Array} tasks - Array of task objects for drawer navigation
 * @returns {Function} handleClick - Function to handle task click with taskId parameter
 */
export const useTaskDetailsDrawer = (tasks) => {
	const engagement = useSelector((store) => store.engagement);
	const emit = useMessageHubEmit(messageHub.kronosMFE);
	const documentId = useDocumentId();
	const [isMFE] = useMFE();

	const navigateToTask = (id) => {
		const url = Utility.getTaskUrl(id, undefined, engagement, undefined, undefined, undefined, undefined, documentId);
		window.open(url, '_blank');
	};

	const handleClick = (taskId) => {
		if (isMFE) {
			requestAnimationFrame(() => {
				if (!engagement?.engagementId || !taskId || !tasks?.length) return;

				const tasksList = tasks.map((t) => ({
					id: t.taskId || t.id,
					taskId: t.taskId || t.id
				}));

				emit(messageType.taskDrawer, {
					engagementId: engagement.engagementId,
					tasks: tasksList,
					selectedTaskId: taskId
				});
			});
		} else {
			navigateToTask(taskId);
		}
	};

	return handleClick;
};

/**
 * Custom hook to manage MFE task drawer open/close state.
 * Listens for task drawer messages from the MFE message hub and provides
 * the current drawer state for use in disabling UI interactions.
 *
 * Only sets up listeners when in MFE mode - returns false otherwise.
 *
 * @param {Object} options - Optional configuration object
 * @param {Function} options.onClose - Callback function invoked when the drawer closes
 * @returns {boolean} isMfeDrawerOpen - Whether the MFE task drawer is currently open
 *
 * @example
 * // Basic usage - just track state
 * const isMfeDrawerOpen = useMfeTaskDrawerState();
 *
 * @example
 * // With close callback - refresh data when drawer closes
 * const isMfeDrawerOpen = useMfeTaskDrawerState(() => refreshData());
 */
export const useMfeTaskDrawerState = (onClose = null) => {
	const [isMfeDrawerOpen, setIsMfeDrawerOpen] = useState(false);
	const [isMFE] = useMFE();
	const onCloseRef = useRef(onClose);

	// Keep ref updated to avoid stale closures
	useEffect(() => {
		onCloseRef.current = onClose;
	}, [onClose]);

	const handleTaskDrawerMessage = useCallback((message) => {
		if (message.drawerClosed) {
			setIsMfeDrawerOpen(false);
			onCloseRef.current?.();
		} else if (message.selectedTaskId) {
			setIsMfeDrawerOpen(true);
		}
	}, []);

	// Only listen when in MFE mode
	useMessageHubListener(messageHub.kronosMFE, messageType.taskDrawer, isMFE ? handleTaskDrawerMessage : undefined);

	return isMFE ? isMfeDrawerOpen : false;
};

export function useResourceUpdates() {
	return useResourceUpdatesCore(manager);
}

export function useSummaryResponseText(smartEvidenceSourceId, documentTypeId, summaryTypeId) {
	const selectResponseText = useMemo(
		() =>
			createSelector(
				(state) => state?.formActions?.summaryResponses?.byId,
				(byId) => {
					const deficiencySummary = byId?.[smartEvidenceSourceId];
					const forms = (deficiencySummary?.forms ?? []).filter((form) => form?.documentTypeId === documentTypeId);
					const responses = forms.flatMap((form) => form?.responses ?? []).filter((r) => r?.summaryTypeId === summaryTypeId);
					if (responses.length === 0) return null;
					const texts = responses.map((r) => r?.responseText?.trim() ?? '').filter((text) => text.length > 0);
					if (texts.length === 0) return '';
					return texts.join('<br/>');
				}
			),
		[smartEvidenceSourceId, documentTypeId, summaryTypeId]
	);

	return useSelector(selectResponseText);
}

const DEFAULT_DOCUMENT_TYPES = [documentTypes.DEFICIENCY_INDIVIDUAL, documentTypes.DEFICIENCY_INDIVIDUAL_ITGC_DEFICIENCY];

export const getActionsByType = (selectedActions, smartEvidenceSourceId, actionTypeId, validDocumentTypes = DEFAULT_DOCUMENT_TYPES) => {
	if (!smartEvidenceSourceId) return [];
	const actionsById = selectedActions?.byId ?? {};
	return Object.values(actionsById).filter(
		(action) =>
			action?.smartEvidenceSourceId === smartEvidenceSourceId && action?.actionTypeId === actionTypeId && validDocumentTypes.includes(action?.documentTypeId)
	);
};
